import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { doc, getDoc, setDoc, getDocs, collection, serverTimestamp, Timestamp } from 'firebase/firestore'
import { db, APP_COLLECTION, APP_DOC } from '../lib/firebase'
import { useAuth } from './AuthContext'
import {
  SEED_COURSES, publishedModules, getModule,
  modules as builtInModules,
} from '../data/modules'
import { myCourses, otherCourses, DEFAULT_COURSES } from '../data/courses'
import { mergeCatalogue, requiredInCourse } from '../data/catalogue'
import {
  degreesForPerson, coursesForDegrees, findDegree,
} from '../data/degrees'
import { courseStats, degreeStats, averageScore, rollbackCompletion } from '../data/progressStats'
import { toMillis, lastCompletedAt } from '../lib/lessonScoring'
import { fallbackProgress } from '../lib/progressLoad'
import { isCompletionStale } from '../data/contentFreshness'
import { useConfig } from './ConfigContext'

const ProgressContext = createContext()

const STORAGE_KEY = 'sheets-training-progress'

const EMPTY_TRACK_PROGRESS = Object.freeze({
  completed: 0, total: 0, percent: 0, optionalCompleted: 0, optionalTotal: 0, started: false,
})

function loadLocalProgress() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) return JSON.parse(saved)
  } catch {}
  return {}
}

function saveLocalProgress(progress) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
  } catch {}
}

export function ProgressProvider({ children }) {
  const { user } = useAuth()
  const { degrees: allDegrees, departments, customModules, allCourses } = useConfig()
  const [moduleProgress, setModuleProgress] = useState({})
  // Which account the progress in hand belongs to. `undefined` means nothing
  // has been loaded yet; `null` means it was loaded for a signed-out visitor.
  const [loadedUid, setLoadedUid] = useState(undefined)

  /**
   * Whether the progress in hand does NOT yet belong to the signed-in account.
   *
   * Derived rather than a boolean set inside the effect below, because the
   * effect runs AFTER the render that first sees a new uid. For that one commit
   * a `setLoading(true)` has not happened yet, so the previous account's — or
   * the signed-out visitor's — progress reads as loaded, and anything deciding
   * what to draw in that commit decides from the wrong record. That is the
   * flash `docs/completion-truth.md` calls C6.
   */
  const loading = loadedUid !== (user?.uid ?? null)

  // Load progress from Firestore or localStorage
  useEffect(() => {
    let cancelled = false

    async function loadFromFirestore() {
      if (!user?.uid) {
        setModuleProgress(fallbackProgress({ anonymous: loadLocalProgress() }))
        setLoadedUid(null)
        return
      }

      try {
        const progressRef = collection(db, APP_COLLECTION, APP_DOC, 'users', user.uid, 'progress')
        const snapshot = await getDocs(progressRef)
        const progress = {}
        snapshot.forEach((doc) => {
          progress[doc.id] = doc.data()
        })
        if (!cancelled) {
          setModuleProgress(progress)
          setLoadedUid(user.uid)
        }
      } catch (err) {
        console.error('Failed to load progress from Firestore:', err)
        if (!cancelled) {
          // NOT the shared localStorage key. It has no account in it, so on a
          // shared machine it would hand this trainee somebody else's
          // completions — and completion is now read from this map, so they
          // would be shown as theirs. See src/lib/progressLoad.js.
          setModuleProgress(fallbackProgress({ uid: user.uid }))
          // Marked loaded even though it failed. Left unmarked, every consumer
          // waits forever on an offline trainee and no lesson ever renders.
          setLoadedUid(user.uid)
        }
      }
    }

    loadFromFirestore()
    return () => { cancelled = true }
  }, [user?.uid])

  // Persist to localStorage as fallback
  useEffect(() => {
    if (!user?.uid) {
      saveLocalProgress(moduleProgress)
    }
  }, [moduleProgress, user?.uid])

  /**
   * Write a module's progress, and SAY WHETHER IT LANDED.
   *
   * Returns `{ ok, error }` rather than throwing. The swallowed-error version
   * of this function meant a caller could `await` it, get no error, show the
   * trainee "module complete", and have written nothing at all — offline, or
   * rejected by rules. Returning a result instead of throwing keeps the
   * existing callers (Quiz, Module0, startModule) behaving exactly as before
   * while letting the ones that make a claim to the trainee check first.
   *
   * `localData` exists because `serverTimestamp()` is a SENTINEL, not a time:
   * no `toDate`, and `new Date()` of it is Invalid Date. Firestore swaps it for
   * the server clock on write, but whatever goes into React state stays there
   * until the next page load — so a sentinel in state made every local reader
   * see "never" for a field that had just been set. Callers writing one pass a
   * local equivalent here; the WRITE keeps the sentinel, because the server's
   * clock is the one to trust.
   */
  const saveModuleProgress = useCallback(async (moduleId, data, localData = data) => {
    const key = String(moduleId)
    setModuleProgress((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...localData },
    }))

    if (!user?.uid) {
      // Signed out: localStorage is the store, and the effect above persists it.
      return { ok: true, local: true }
    }

    try {
      const docRef = doc(db, APP_COLLECTION, APP_DOC, 'users', user.uid, 'progress', key)
      await setDoc(docRef, data, { merge: true })
      return { ok: true }
    } catch (err) {
      console.error('[saveModuleProgress] Firestore write FAILED:', err)
      return { ok: false, error: err }
    }
  }, [user?.uid])

  /**
   * The module as it exists now — the custom one wins, matching ModuleRouter
   * and mergeCatalogue, because a custom module sharing an id OVERRIDES the
   * built-in and is therefore the thing the trainee actually sat.
   */
  const lookupModule = useCallback(
    (moduleId) =>
      (customModules || []).find((m) => String(m.id) === String(moduleId)) || getModule(moduleId),
    [customModules],
  )

  const isModuleCompleted = useCallback((moduleId) => {
    const key = String(moduleId)
    if (moduleProgress[key]?.completed !== true) return false
    // A completion on a module that has since changed does not read as done:
    // the same rule progressStats applies to the percentages, so the card and
    // the progress bar cannot disagree. Nothing is deleted — see
    // contentFreshness.js — so accepting the completions restores both.
    return !isCompletionStale(lookupModule(moduleId), moduleProgress[key])
  }, [moduleProgress, lookupModule])

  const startModule = useCallback(async (moduleId) => {
    const key = String(moduleId)
    if (moduleProgress[key]?.startedAt) return // already started

    const data = {
      moduleId,
      startedAt: user?.uid ? serverTimestamp() : new Date().toISOString(),
    }
    // A real time in state, so completing a module in the same page session
    // that started it can still work out how long it took. It could not before:
    // the sentinel is not a Timestamp, so `new Date(sentinel)` was an Invalid
    // Date and timeSpentMs was written as NaN.
    return saveModuleProgress(moduleId, data,
      user?.uid ? { ...data, startedAt: Timestamp.now() } : data)
  }, [moduleProgress, saveModuleProgress, user?.uid])

  const saveQuizResults = useCallback(async (moduleId, quizResults) => {
    const data = {
      quizResults,
    }
    return saveModuleProgress(moduleId, data)
  }, [saveModuleProgress])


  const completeModule = useCallback(async (moduleId, quizScore) => {
    const key = String(moduleId)
    const existing = moduleProgress[key] || {}
    const now = user?.uid ? serverTimestamp() : new Date().toISOString()

    let timeSpentMs = 0
    if (existing.startedAt) {
      const startTime = existing.startedAt instanceof Timestamp
        ? existing.startedAt.toDate()
        : new Date(existing.startedAt)
      timeSpentMs = Date.now() - startTime.getTime()
    }

    const data = {
      moduleId,
      completed: true,
      completedAt: now,
      timeSpentMs,
    }

    // Likewise a real time in state. A read-through stores no quizResults, so
    // this field is the only record of it having been finished — and while it
    // held a sentinel, `lastCompletedAt` read 0 and a reissue stayed pending
    // even after the trainee finished, until they reloaded the page.
    const result = await saveModuleProgress(moduleId, data,
      user?.uid ? { ...data, completedAt: Timestamp.now() } : data)

    // Undo the optimistic merge if the write did not land. Left in place it
    // reads as a completion the server never received — the module shows as
    // done, and `lastCompletedAt` treats an admin's retake request as already
    // satisfied. A signed-out save returns ok, so this fires only on a real
    // Firestore failure.
    if (result && result.ok === false) {
      setModuleProgress((prev) => ({
        ...prev,
        [key]: rollbackCompletion(prev[key], existing),
      }))
    }
    return result
  }, [moduleProgress, saveModuleProgress, user?.uid])

  const resetProgress = useCallback(async () => {
    setModuleProgress({})
    if (user?.uid) {
      try {
        const progressRef = collection(db, APP_COLLECTION, APP_DOC, 'users', user.uid, 'progress')
        const snapshot = await getDocs(progressRef)
        const { deleteDoc } = await import('firebase/firestore')
        const deletes = snapshot.docs.map((d) => deleteDoc(d.ref))
        await Promise.all(deletes)
      } catch (err) {
        console.error('Failed to reset progress in Firestore:', err)
      }
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [user?.uid])

  const getModuleProgress = useCallback((moduleId) => {
    return moduleProgress[String(moduleId)] || null
  }, [moduleProgress])

  // Get module IDs that have been reissued but not yet retaken
  /** Completed, but the module has changed since — retake to reclaim the score. */
  const isModuleStale = useCallback(
    (moduleId) => isCompletionStale(lookupModule(moduleId), moduleProgress[String(moduleId)]),
    [lookupModule, moduleProgress],
  )

  const getStaleModules = useCallback(
    () => Object.keys(moduleProgress).filter((id) => isModuleStale(id)).map((id) => lookupModule(id)?.id ?? id),
    [moduleProgress, isModuleStale, lookupModule],
  )

  const getReissuedModules = useCallback(() => {
    const reissued = []
    for (const mod of publishedModules()) {
      const mp = moduleProgress[String(mod.id)]
      if (!mp?.reissued) continue
      const reissuedAt = toMillis(mp.reissued)
      // Not quizResults alone: a read-through module stores no results, so that
      // read 0 and left the reissue pending forever — the trainee is bounced
      // back to the module every time they finish it.
      if (reissuedAt > lastCompletedAt(mp)) {
        reissued.push(mod.id)
      }
    }
    return reissued
  }, [moduleProgress])

  /**
   * Completion per track. The denominator is published, non-optional modules
   * in that track only — otherwise a Finance trainee who finishes everything
   * asked of them reads as 35% because the CS track is in the divisor, and
   * anyone who skips the optional install walkthrough is capped below 100.
   */
  const progressByCourse = useMemo(() => {
    const out = {}
    for (const t of (allCourses || SEED_COURSES)) {
      // The COURSE, not its id — courseStats reads its ordered module list.
      out[t.id] = courseStats(moduleProgress, t, customModules || [])
    }
    return out
  }, [moduleProgress, allCourses, customModules])

  const progressForCourse = useCallback(
    (courseId) => progressByCourse[courseId] || EMPTY_TRACK_PROGRESS,
    [progressByCourse]
  )

  /**
   * The courses this trainee has started — any module of the course with a
   * progress document against it.
   */
  const startedCourseIds = useMemo(() => {
    return (allCourses || SEED_COURSES)
      .filter((t) => courseStats(moduleProgress, t, customModules || []).engaged)
      .map((t) => t.id)
  }, [moduleProgress, allCourses, customModules])

  /**
   * The degrees this trainee holds: conferred by their department, plus any
   * awarded to them individually.
   */
  const myDegreeIds = useMemo(
    () => degreesForPerson(
      { department: user?.department, degrees: user?.degrees },
      departments,
      allDegrees
    ),
    [user?.department, user?.degrees, departments, allDegrees]
  )

  /**
   * Courses from those degrees, plus individually assigned, plus started.
   *
   * `allCourses` is passed in, and that is the fix: this resolved against the
   * four hardcoded seed courses, so a course an admin created could never be
   * anybody's however deliberately it was given to them. `user.assignedTracks`
   * keeps its name because it is a Firestore field, not an identifier.
   */
  const myCourseIds = useMemo(
    () => myCourses({
      assigned: user?.assignedTracks,
      started: startedCourseIds,
      degreeCourses: coursesForDegrees(myDegreeIds, allDegrees, allCourses || SEED_COURSES, customModules || []),
    }, allCourses || DEFAULT_COURSES, customModules || []),
    [user?.assignedTracks, startedCourseIds, myDegreeIds, allDegrees, allCourses, customModules]
  )

  /**
   * Completion per degree: the distinct required modules across all of its
   * courses. A module inside two degrees counts towards both, which is the
   * point — nobody repeats the generic Claude course because they picked up a
   * second degree.
   */
  const degreeProgress = useMemo(() => {
    const out = {}
    for (const degree of allDegrees) {
      out[degree.id] = degreeStats(moduleProgress, degree.id, allDegrees, allCourses || SEED_COURSES, customModules || [])
    }
    return out
  }, [moduleProgress, allDegrees, allCourses, customModules])

  const progressForDegree = useCallback(
    (degreeId) => degreeProgress[degreeId] || {
      completed: 0, total: 0, percent: 0, optionalCompleted: 0, optionalTotal: 0, courseCount: 0,
    },
    [degreeProgress]
  )

  /** Their average across every module they have a score for, or null. */
  const myAverageScore = useMemo(() => averageScore(moduleProgress), [moduleProgress])

  /** The degrees this trainee holds, as objects, in declaration order. */
  const myDegrees = useMemo(
    () => myDegreeIds.map((id) => findDegree(allDegrees, id)).filter(Boolean),
    [myDegreeIds, allDegrees]
  )

  // Through otherCourses rather than "everything minus mine", so a course an
  // admin has hidden, or one with nothing published in it, stops appearing in
  // the browse pile. That filter existed in the helper all along; this had
  // reimplemented it without the visibility half.
  const otherCourseIds = useMemo(
    () => otherCourses(myCourseIds, allCourses || DEFAULT_COURSES, customModules || []),
    [allCourses, myCourseIds, customModules]
  )

  /**
   * One number across the trainee's OWN courses.
   */
  const overallProgress = useMemo(() => {
    const catalogue = mergeCatalogue(builtInModules, customModules || [])
    // DISTINCT across their courses. A module in two of them is one module to
    // do, not two — the same correction degreeStats needed.
    const seen = new Set()
    const required = []
    for (const id of myCourseIds) {
      const course = (allCourses || SEED_COURSES).find((t) => String(t.id) === String(id)) || { id }
      for (const mod of requiredInCourse(course, catalogue)) {
        const key = String(mod.id)
        if (seen.has(key)) continue
        seen.add(key)
        required.push(mod)
      }
    }
    if (!required.length) return 0
    const done = required.filter((m) => moduleProgress[String(m.id)]?.completed === true).length
    return Math.round((done / required.length) * 100)
  }, [moduleProgress, myCourseIds, customModules, allCourses])

  return (
    <ProgressContext.Provider
      value={{
        progress: moduleProgress,
        loading,
        isModuleCompleted,
        completeModule,
        resetProgress,
        overallProgress,
        progressByCourse,
        progressForCourse,
        startModule,
        saveQuizResults,
        myCourseIds,
        otherCourseIds,
        startedCourseIds,
        myDegrees,
        degreeProgress,
        progressForDegree,
        myAverageScore,
        saveModuleProgress,
        getModuleProgress,
        getReissuedModules,
        isModuleStale,
        getStaleModules,
      }}
    >
      {children}
    </ProgressContext.Provider>
  )
}

export function useProgress() {
  return useContext(ProgressContext)
}
